import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";

export const crmInput = "w-full bg-[#F3F0F5] border-none rounded-lg px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-brand-purple/20 focus:bg-white outline-none transition-all";

export function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-brand-purpleDeep/60 mb-1.5 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-brand-purpleDeep/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          onClick={onClose}
          data-testid="crm-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
            className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} my-8`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="font-heading font-bold text-lg text-brand-purpleDeep">{title}</h3>
              <button onClick={onClose} className="text-brand-purpleDeep/40 hover:text-brand-purpleDeep" data-testid="modal-close"><X size={20} /></button>
            </div>
            <div className="p-6">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-700", Contacted: "bg-amber-100 text-amber-700",
  Qualified: "bg-purple-100 text-purple-700", "Proposal Sent": "bg-orange-100 text-orange-700",
  Won: "bg-green-100 text-green-700", Lost: "bg-red-100 text-red-700",
  Planning: "bg-slate-100 text-slate-700", "Pre-production": "bg-indigo-100 text-indigo-700",
  Filming: "bg-blue-100 text-blue-700", Editing: "bg-purple-100 text-purple-700",
  "Client Review": "bg-amber-100 text-amber-700", Revisions: "bg-orange-100 text-orange-700",
  Approved: "bg-green-100 text-green-700", Completed: "bg-green-100 text-green-700",
  Todo: "bg-slate-100 text-slate-700", "In Progress": "bg-blue-100 text-blue-700", Done: "bg-green-100 text-green-700",
  Low: "bg-slate-100 text-slate-600", Medium: "bg-blue-100 text-blue-700", High: "bg-orange-100 text-orange-700", Urgent: "bg-red-100 text-red-700",
};

export function Badge({ children, tone }) {
  const cls = STATUS_COLORS[children] || tone || "bg-brand-light text-brand-purple";
  return <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-semibold ${cls}`}>{children}</span>;
}

export function EmptyState({ title, hint, action }) {
  return (
    <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl" data-testid="empty-state">
      <p className="font-heading font-bold text-brand-purpleDeep">{title}</p>
      {hint && <p className="mt-1 text-sm text-brand-purpleDeep/50">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function useNotifications() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/notifications").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);
  const unread = items.filter((i) => !i.read).length;
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return { items, unread, markAll, reload: load };
}
